/**
 * <ry-toggle-button>
 *
 * Standalone toggle button that works as part of a group via shared `name` attribute.
 * Buttons with the same name behave like radio buttons - only one can be pressed at a time.
 *
 * Usage:
 * <ry-toggle-button name="view" value="list" icon="list"></ry-toggle-button>
 * <ry-toggle-button name="view" value="grid" icon="grid" pressed></ry-toggle-button>
 *
 * Conflict detection:
 * Warns in console if same-name buttons span different form/section/fieldset containers.
 */

import { RyElement } from '../core/ry-element.js';
import type { ChangeDetail } from '../types.js';

// Container types that define logical grouping boundaries
const BOUNDARY_SELECTORS = ['form', 'section', 'fieldset', 'ry-section'];

// Global registry: name -> Set of toggle button instances
const registry = new Map<string, Set<RyToggleButton>>();

// Track which name groups have already warned about conflicts
const warnedGroups = new Set<string>();

/**
 * Find the nearest boundary ancestor (form, section, fieldset)
 */
function findBoundaryAncestor(el: Element): Element | null {
  let current = el.parentElement;
  while (current) {
    if (BOUNDARY_SELECTORS.some(sel => current!.matches(sel))) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Check if buttons with the same name are in different boundary containers
 */
function checkForConflicts(name: string): void {
  if (warnedGroups.has(name)) return;

  const buttons = registry.get(name);
  if (!buttons || buttons.size < 2) return;

  const boundaries = new Map<Element | null, RyToggleButton[]>();

  for (const btn of buttons) {
    const boundary = findBoundaryAncestor(btn);
    const group = boundaries.get(boundary) ?? [];
    group.push(btn);
    boundaries.set(boundary, group);
  }

  // If buttons are in more than one boundary container, warn
  if (boundaries.size > 1) {
    const containerDescriptions: string[] = [];
    for (const [boundary] of boundaries) {
      if (boundary) {
        const id = boundary.id ? `#${boundary.id}` : '';
        const tag = boundary.tagName.toLowerCase();
        containerDescriptions.push(`${tag}${id}`);
      } else {
        containerDescriptions.push('(no container)');
      }
    }

    console.warn(
      `ry-toggle-button: Buttons with name="${name}" span multiple containers (${containerDescriptions.join(', ')}). Intentional?`
    );
    warnedGroups.add(name);
  }
}

export class RyToggleButton extends RyElement {
  static observedAttributes = ['pressed', 'disabled', 'name', 'value'] as const;

  #registered = false;

  setup(): void {
    // Set up accessibility
    this.setAttribute('role', 'button');
    this.setAttribute('aria-pressed', String(this.pressed));

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Register in the global registry
    this.#register();

    // Event handlers
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  teardown(): void {
    this.#unregister();
  }

  #register(): void {
    const name = this.name;
    if (!name) return;

    if (!registry.has(name)) {
      registry.set(name, new Set());
    }
    registry.get(name)!.add(this);
    this.#registered = true;

    // Check for conflicts after a microtask to allow all buttons to register
    queueMicrotask(() => checkForConflicts(name));
  }

  #unregister(): void {
    if (!this.#registered) return;

    const name = this.name;
    if (name && registry.has(name)) {
      registry.get(name)!.delete(this);
      if (registry.get(name)!.size === 0) {
        registry.delete(name);
        warnedGroups.delete(name);
      }
    }
    this.#registered = false;
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    // Toggle this button on (don't toggle off if already pressed)
    if (!this.pressed) {
      this.pressed = true;
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.click();
    }
  };

  #unpressOthers(): void {
    const name = this.name;
    if (!name) return;

    const siblings = registry.get(name);
    if (!siblings) return;

    for (const btn of siblings) {
      if (btn !== this && btn.pressed) {
        btn.pressed = false;
      }
    }
  }

  // --- Public API ---

  get pressed(): boolean {
    return this.hasAttribute('pressed');
  }

  set pressed(value: boolean) {
    const wasPressed = this.pressed;

    if (value) {
      this.setAttribute('pressed', '');
      this.setAttribute('aria-pressed', 'true');
      this.#unpressOthers();
    } else {
      this.removeAttribute('pressed');
      this.setAttribute('aria-pressed', 'false');
    }

    // Only emit if state actually changed
    if (wasPressed !== value) {
      this.emit<ChangeDetail>('change', {
        value: this.value,
        label: value ? 'pressed' : 'unpressed'
      });
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(value: boolean) {
    if (value) {
      this.setAttribute('disabled', '');
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else {
      this.removeAttribute('disabled');
      this.removeAttribute('aria-disabled');
      this.setAttribute('tabindex', '0');
    }
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    const oldName = this.name;
    if (oldName === value) return;

    // Re-register with new name
    this.#unregister();
    if (value) {
      this.setAttribute('name', value);
    } else {
      this.removeAttribute('name');
    }
    this.#register();
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    this.setAttribute('value', val);
  }

  get icon(): string {
    return this.getAttribute('icon') ?? '';
  }

  set icon(val: string) {
    this.setAttribute('icon', val);
  }
}

customElements.define('ry-toggle-button', RyToggleButton);
